import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { DayButton, DayPicker, type DropdownProps, getDefaultClassNames } from 'react-day-picker';
import { ChevronDown } from '../../components/svgs/icons/ChevronDown';
import { ChevronLeft } from '../../components/svgs/icons/ChevronLeft';
import { ChevronRight } from '../../components/svgs/icons/ChevronRight';
import { Button } from '../Button';
import { s as buttonStyles } from '../Button/styles';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Select';
import { s } from './styles';

/**
 * The element the caption Selects portal into. A Calendar is usually inside a popover, and a popup
 * portaled to `<body>` reads as an outside click there, so the popover would close on every month
 * or year pick.
 */
const CalendarPortalContainerContext = React.createContext<HTMLElement | null>(null);

function Calendar({
	styles = [],
	classNames,
	showOutsideDays = true,
	captionLayout = 'label',
	buttonVariant = 'ghost',
	formatters,
	components,
	...props
}: React.ComponentProps<typeof DayPicker> & {
	buttonVariant?: React.ComponentProps<typeof Button>['variant'];
	styles?: StyleDeclarationValue[];
}) {
	const defaultClassNames = getDefaultClassNames();
	// `display: contents` keeps this host out of the layout; it exists only to be portaled into.
	const [portalContainer, setPortalContainer] = React.useState<HTMLDivElement | null>(null);
	return (
		<div ref={setPortalContainer} className={css(s.portalHost)}>
			<CalendarPortalContainerContext.Provider value={portalContainer}>
				<DayPicker
					showOutsideDays={showOutsideDays}
					className={css(s.calendar, styles)}
					captionLayout={captionLayout}
					formatters={{
						formatMonthDropdown: date => date.toLocaleString('default', { month: 'short' }),
						...formatters,
					}}
					classNames={{
						root: `${css(s.root)} ${defaultClassNames.root}`,
						months: `${css(s.months)} ${defaultClassNames.months}`,
						month: `${css(s.month)} ${defaultClassNames.month}`,
						nav: `${css(s.nav)} ${defaultClassNames.nav}`,
						button_previous: `${css(buttonStyles.base, buttonStyles[buttonVariant], s.navButton)} ${defaultClassNames.button_previous}`,
						button_next: `${css(buttonStyles.base, buttonStyles[buttonVariant], s.navButton)} ${defaultClassNames.button_next}`,
						month_caption: `${css(s.monthCaption)} defaultClassNames.month_caption`,
						dropdowns: `${css(s.dropdowns)} ${defaultClassNames.dropdowns}`,
						caption_label: `${css(
							s.captionLabel,
							captionLayout === 'label' ? s.captionLabelText : s.captionLabelButton
						)} ${defaultClassNames.caption_label}`,
						table: css(s.table),
						weekdays: `${css(s.weekdays)} ${defaultClassNames.weekdays}`,
						weekday: `${css(s.weekday)} ${defaultClassNames.weekday}`,
						week: `${css(s.week)} ${defaultClassNames.week}`,
						week_number_header: `${css(s.weekNumberHeader)} ${defaultClassNames.week_number_header}`,
						week_number: `${css(s.weekNumber)} ${defaultClassNames.week_number}`,
						day: `${css(s.day)} ${defaultClassNames.day}`,
						range_start: `${css(s.rangeStart)} ${defaultClassNames.range_start}`,
						range_middle: `${css(s.rangeMiddle)} ${defaultClassNames.range_middle}`,
						range_end: `${css(s.rangeEnd)} ${defaultClassNames.range_end}`,
						today: `${css(s.today)} ${defaultClassNames.today}`,
						outside: `${css(s.outside)} ${defaultClassNames.outside}`,
						disabled: `${css(s.disabled)} ${defaultClassNames.disabled}`,
						hidden: `${css(s.hidden)} ${defaultClassNames.hidden}`,
						...classNames,
					}}
					components={{
						Root: ({ className, rootRef, ...rest }) => {
							return <div data-slot='calendar' ref={rootRef} className={className} {...rest} />;
						},
						Chevron: ({ className, orientation, ...rest }) => {
							if (orientation === 'left') {
								return <ChevronLeft className={className} width={16} height={16} {...rest} />;
							}
							if (orientation === 'right') {
								return <ChevronRight className={className} width={16} height={16} {...rest} />;
							}
							return <ChevronDown className={className} width={16} height={16} {...rest} />;
						},
						DayButton: CalendarDayButton,
						MonthsDropdown: CalendarDropdown,
						YearsDropdown: CalendarDropdown,
						WeekNumber: ({ children, ...rest }) => {
							return (
								<td {...rest}>
									<div className={css(s.weekNumberCell)}>{children}</div>
								</td>
							);
						},
						...components,
					}}
					{...props}
				/>
			</CalendarPortalContainerContext.Provider>
		</div>
	);
}

/**
 * Month and year pickers for `captionLayout='dropdown'`. React Day Picker ships a native `<select>`
 * here, which renders as an unstyled OS menu; this swaps in the components-v2 Select. The library's
 * own handler only reads `target.value`, so a minimal synthetic event carries the pick back.
 */
function CalendarDropdown({ options, value, onChange, disabled, 'aria-label': ariaLabel }: DropdownProps) {
	const portalContainer = React.useContext(CalendarPortalContainerContext);
	const items = options ?? [];
	return (
		<Select
			items={items}
			value={value as number}
			disabled={disabled}
			onValueChange={next => onChange?.({ target: { value: String(next) } } as React.ChangeEvent<HTMLSelectElement>)}
		>
			<SelectTrigger aria-label={ariaLabel} styles={s.captionSelectTrigger}>
				<SelectValue />
			</SelectTrigger>
			<SelectContent container={portalContainer ?? undefined} styles={s.captionSelectContent}>
				{items.map(option => (
					<SelectItem key={option.value} value={option.value} disabled={option.disabled}>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

function CalendarDayButton({ className, day, modifiers, ...props }: React.ComponentProps<typeof DayButton>) {
	const defaultClassNames = getDefaultClassNames();
	const ref = React.useRef<HTMLButtonElement>(null);
	React.useEffect(() => {
		if (modifiers.focused) ref.current?.focus();
	}, [modifiers.focused]);

	return (
		<Button
			ref={ref}
			variant='ghost'
			size='icon'
			data-day={day.date.toLocaleDateString()}
			data-selected-single={
				modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
			}
			data-range-start={modifiers.range_start}
			data-range-end={modifiers.range_end}
			data-range-middle={modifiers.range_middle}
			styles={[s.calendarDayButton]}
			selectorClassName={`calendar-day-button ${defaultClassNames.day} ${className}`}
			{...props}
		/>
	);
}
export { Calendar, CalendarDayButton };
